/**
 * ContractsAdminPanel — "Contracts" admin tab
 * Composes RuleListView + rule editor + ContractSigningActionEditor.
 * Sets context = "contract_signing" automatically.
 * Spec: Sections 8, 13
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { rulesEngineService } from '../../services/api/rulesEngineService';
import { bankService } from '../../services/api/bankService';
import type { Bank } from '../../services/api/bankService';
import { clientService } from '../../services/api/clientService';
import type { TradingEntity, AutoContractAnalysisSettings } from '../../services/api/clientService';
import { groupingsService } from '../../services/api/groupingsService';
import type { GroupingsConfig } from '../../types/groupings';
import RuleListView from '../rules/RuleListView';
import RuleConditionBuilder from '../rules/RuleConditionBuilder';
import ContractSigningActionEditor from './ContractSigningActionEditor';
import AlertModal from '../common/AlertModal';
import type { Rule, RuleCreate, RuleUpdate, ConditionGroup, ConflictResult, FieldCatalogueEntry } from '../../types/rulesEngine';
import './ContractsAdminPanel.css';

const CONTEXT = 'contract_signing';

type ViewMode = 'list' | 'create' | 'edit';

const DEFAULT_ACTION: Record<string, unknown> = {
  platform: 'fx_plus',
  signatureType: 'simple',
  signatoryCount: 0,
  signatoryFlow: 'parallel',
  signatories: [],
  approvers: [],
  approverMoment: null,
};

const ContractsAdminPanel: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || '';

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Editor form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [conditionGroups, setConditionGroups] = useState<ConditionGroup[]>([]);
  const [action, setAction] = useState<Record<string, unknown>>(DEFAULT_ACTION);

  // Dynamic field catalogue data
  const [banks, setBanks] = useState<Bank[]>([]);
  const [tradingEntities, setTradingEntities] = useState<TradingEntity[]>([]);
  const [groupingsConfig, setGroupingsConfig] = useState<GroupingsConfig | null>(null);

  // Auto-contract analysis settings (KAN-195)
  const [autoContractSettings, setAutoContractSettings] = useState<AutoContractAnalysisSettings>({
    enabled: false, lookbackDays: 10, retryAttempts: 3, retryDelayMinutes: 5,
  });
  const [autoContractSaving, setAutoContractSaving] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const digitalSignatureEnabled = user?.profile?.organization?.digitalSignatureEnabled ?? false;

  // UI state
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState<ConflictResult[]>([]);
  const [alertMessage, setAlertMessage] = useState('');

  // Load dynamic data for field catalogue + auto-contract settings
  useEffect(() => {
    if (!clientId) return;
    const load = async () => {
      const [banksRes, entitiesRes, groupings, settingsRes] = await Promise.all([
        bankService.listBanks().catch(() => null),
        clientService.getTradingEntities(clientId).catch(() => null),
        groupingsService.getGroupings(clientId).catch(() => null),
        clientService.getClientSettings(clientId).catch(() => null),
      ]);
      if (banksRes?.success && banksRes.data) setBanks(banksRes.data);
      if (entitiesRes?.success && entitiesRes.data) setTradingEntities(entitiesRes.data);
      if (groupings) setGroupingsConfig(groupings);
      if (settingsRes?.success && settingsRes.data?.automation?.autoContractAnalysis) {
        setAutoContractSettings(settingsRes.data.automation.autoContractAnalysis);
      }
      setInitialLoading(false);
    };
    load();
  }, [clientId]);

  // Build field catalogue dynamically from loaded data
  const fieldCatalogue = useMemo<FieldCatalogueEntry[]>(() => {
    const NUMBER_OPERATORS: FieldCatalogueEntry['operators'] = [
      'equals', 'not_equals', 'greater_than', 'less_than',
      'greater_than_or_equal', 'less_than_or_equal', 'between',
    ];
    const DATE_OPERATORS = NUMBER_OPERATORS;

    const catalogue: FieldCatalogueEntry[] = [
      {
        displayName: 'Product Type',
        field: 'ProductType',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in'],
        inputType: 'dropdown',
        options: [
          { label: 'Spot', value: 'Spot' },
          { label: 'Forward', value: 'Forward' },
          { label: 'Forward Asiático', value: 'Forward Asiático' },
          { label: 'Forward Starting', value: 'Forward Starting' },
        ],
      },
      {
        displayName: 'Settlement Type',
        field: 'SettlementType',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in'],
        inputType: 'dropdown',
        options: [
          { label: 'Entrega Física', value: 'Entrega Física' },
          { label: 'Compensación', value: 'Compensación' },
        ],
      },
      {
        displayName: 'Direction',
        field: 'Direction',
        valueType: 'string',
        operators: ['equals', 'not_equals'],
        inputType: 'dropdown',
        options: [
          { label: 'Buy', value: 'Buy' },
          { label: 'Sell', value: 'Sell' },
        ],
      },
      {
        displayName: 'Our Party',
        field: 'OurParty',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in'],
        inputType: 'dropdown',
        options: tradingEntities.map(e => ({ label: e.name, value: e.name })),
      },
      {
        displayName: 'Counterparty',
        field: 'CounterpartyName',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in'],
        inputType: 'dropdown',
        options: banks.filter(b => b.isCounterparty !== false).map(b => ({ label: b.name, value: b.name })),
      },
      {
        displayName: 'Currency 1',
        field: 'Currency1',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in'],
        inputType: 'text',
      },
      {
        displayName: 'Currency 2',
        field: 'Currency2',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in'],
        inputType: 'text',
      },
      {
        displayName: 'Notional Amount',
        field: 'QuantityCurrency1',
        valueType: 'number',
        operators: NUMBER_OPERATORS,
        inputType: 'number',
      },
      {
        displayName: 'Contra Amount',
        field: 'QuantityCurrency2',
        valueType: 'number',
        operators: NUMBER_OPERATORS,
        inputType: 'number',
      },
      {
        displayName: 'Trade Date',
        field: 'TradeDate',
        valueType: 'date',
        operators: DATE_OPERATORS,
        inputType: 'datepicker',
      },
      {
        displayName: 'Value Date',
        field: 'ValueDate',
        valueType: 'date',
        operators: DATE_OPERATORS,
        inputType: 'datepicker',
      },
      {
        displayName: 'Maturity Date',
        field: 'MaturityDate',
        valueType: 'date',
        operators: DATE_OPERATORS,
        inputType: 'datepicker',
      },
      {
        displayName: 'Parent Trade Number',
        field: 'internalTradeRef.parentTradeNumber',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in', 'is_empty', 'is_not_empty'],
        inputType: 'text',
      },
      {
        displayName: 'Internal Trade',
        field: 'IsInternalTrade',
        valueType: 'string',
        operators: ['equals', 'not_equals'],
        inputType: 'dropdown',
        options: [
          { label: 'true', value: 'true' },
          { label: 'false', value: 'false' },
        ],
      },
    ];

    // Grouping 1 — only if configured
    if (groupingsConfig?.grouping1Label) {
      const activeValues = groupingsConfig.grouping1Values.filter(v => v.isActive);
      catalogue.push({
        displayName: groupingsConfig.grouping1Label,
        field: 'Grouping1',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in', 'is_empty', 'is_not_empty'],
        inputType: activeValues.length > 0 ? 'dropdown' : 'text',
        options: activeValues.map(v => ({ label: v.name, value: v.name })),
      });
    }

    // Grouping 2 — only if label is set
    if (groupingsConfig?.grouping2Label) {
      const activeValues = groupingsConfig.grouping2Values.filter(v => v.isActive);
      catalogue.push({
        displayName: groupingsConfig.grouping2Label,
        field: 'Grouping2',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in', 'is_empty', 'is_not_empty'],
        inputType: activeValues.length > 0 ? 'dropdown' : 'text',
        options: activeValues.map(v => ({ label: v.name, value: v.name })),
      });
    }

    return catalogue;
  }, [banks, tradingEntities, groupingsConfig]);

  // ── Table summary functions ──

  const OPERATOR_LABELS: Record<string, string> = {
    equals: '=', not_equals: '≠',
    in: '∈', not_in: '∉',
    greater_than: '>', less_than: '<',
    greater_than_or_equal: '≥', less_than_or_equal: '≤',
    between: '↔', contains: '⊃', not_contains: '⊅',
    is_empty: t('rules.operatorIsEmpty', 'is empty'),
    is_not_empty: t('rules.operatorIsNotEmpty', 'is not empty'),
  };

  const conditionSummaryFn = useCallback((rule: Rule): string => {
    if (!rule.conditionGroups || rule.conditionGroups.length === 0) {
      return t('rules.defaultRule', 'Applies to all trades');
    }
    const groupTexts = rule.conditionGroups.map(group => {
      const parts = group.conditions.map(cond => {
        const catalogueEntry = fieldCatalogue.find(f => f.field === cond.field);
        const fieldLabel = catalogueEntry?.displayName || cond.field;
        const op = OPERATOR_LABELS[cond.operator] || cond.operator;

        if (cond.operator === 'is_empty' || cond.operator === 'is_not_empty') {
          return `${fieldLabel} ${op}`;
        }

        let valueStr = '';
        if (Array.isArray(cond.value)) {
          valueStr = cond.value.join(', ');
        } else if (cond.value != null && typeof cond.value === 'object') {
          const obj = cond.value as Record<string, unknown>;
          valueStr = `${obj.from ?? ''}–${obj.to ?? ''}`;
        } else {
          valueStr = String(cond.value ?? '');
        }

        return `${fieldLabel} ${op} ${valueStr}`;
      });
      return parts.join(' & ');
    });
    return groupTexts.join(' | ');
  }, [fieldCatalogue, t]);

  const actionSummaryFn = useCallback((rule: Rule): string => {
    const a = rule.action as Record<string, unknown>;
    const parts: string[] = [];

    const platform = a.platform as string;
    if (platform === 'counterparty_platform') return t('admin.contracts.platformCounterparty', 'Counterparty Platform');
    if (platform === 'manual') return t('admin.contracts.platformManual', 'Physical Signature');

    // FX+ platform
    const sigType = (a.signatureType as string) || 'simple';
    parts.push(sigType === 'advanced' ? t('admin.contracts.signatureTypeAdvanced', 'Advanced') : t('admin.contracts.signatureTypeSimple', 'Simple'));

    if (a.civilRegistryValidation) {
      parts.push(t('rules.summaryRegistry', 'Registry'));
    }

    const signatories = (a.signatories as Array<{ fullName: string }>) || [];
    if (signatories.length > 0) {
      const names = signatories.map(s => s.fullName || '?').join(', ');
      parts.push(`${signatories.length} ${t('rules.summarySigner', { count: signatories.length, defaultValue: signatories.length === 1 ? 'signer' : 'signers' })}: ${names}`);
    }

    const approvers = (a.approvers as Array<{ fullName: string }>) || [];
    if (approvers.length > 0) {
      const names = approvers.map(ap => ap.fullName || '?').join(', ');
      parts.push(`${approvers.length} ${t('rules.summaryApprover', { count: approvers.length, defaultValue: approvers.length === 1 ? 'approver' : 'approvers' })}: ${names}`);
    }

    return parts.join(' · ');
  }, [t]);

  // Save auto-contract analysis settings
  const handleAutoContractToggle = async (enabled: boolean) => {
    const updated = { ...autoContractSettings, enabled };
    setAutoContractSettings(updated);
    setAutoContractSaving(true);
    try {
      await clientService.updateClientSettings(clientId, {
        automation: { autoContractAnalysis: updated },
      });
    } catch {
      setAutoContractSettings(prev => ({ ...prev, enabled: !enabled }));
      setAlertMessage(t('common.error', 'An error occurred'));
    } finally {
      setAutoContractSaving(false);
    }
  };

  const handleAutoContractAdvancedSave = async () => {
    setAutoContractSaving(true);
    try {
      await clientService.updateClientSettings(clientId, {
        automation: { autoContractAnalysis: autoContractSettings },
      });
    } catch {
      setAlertMessage(t('common.error', 'An error occurred'));
    } finally {
      setAutoContractSaving(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setName('');
    setDescription('');
    setIsActive(true);
    setConditionGroups([]);
    setAction({ ...DEFAULT_ACTION });
    setConflicts([]);
  };

  // Enter create mode
  const handleCreate = () => {
    resetForm();
    setEditingRule(null);
    setViewMode('create');
  };

  // Enter edit mode
  const handleEdit = (rule: Rule) => {
    setEditingRule(rule);
    setName(rule.name);
    setDescription(rule.description || '');
    setIsActive(rule.isActive);
    setConditionGroups(rule.conditionGroups);
    setAction(rule.action);
    setConflicts([]);
    setViewMode('edit');
  };

  // Copy rule — create a duplicate immediately and stay on the list
  const handleCopy = async (rule: Rule) => {
    try {
      const payload: RuleCreate = {
        name: `${rule.name} (${t('rules.copyNoun', 'Copy')})`,
        description: rule.description || undefined,
        context: CONTEXT,
        isActive: rule.isActive,
        conditionGroups: rule.conditionGroups,
        action: rule.action,
      };
      const response = await rulesEngineService.createRule(clientId, payload);
      if (response.success && response.data) {
        setConflicts(response.data.conflicts || []);
        setRefreshKey(k => k + 1);
      }
    } catch (err: any) {
      setAlertMessage(err.message || t('common.error', 'An error occurred'));
    }
  };

  // Back to list
  const handleBack = () => {
    setViewMode('list');
    setEditingRule(null);
    setConflicts([]);
  };

  // Save rule
  const handleSave = async () => {
    if (!name.trim()) {
      setAlertMessage(t('rules.nameRequired', 'Rule name is required.'));
      return;
    }

    setSaving(true);
    try {
      if (viewMode === 'create') {
        const payload: RuleCreate = {
          name: name.trim(),
          description: description.trim() || undefined,
          context: CONTEXT,
          isActive,
          conditionGroups,
          action,
        };
        const response = await rulesEngineService.createRule(clientId, payload);
        if (response.success && response.data) {
          setConflicts(response.data.conflicts || []);
          if (!response.data.conflicts?.length) {
            setRefreshKey(k => k + 1);
            setViewMode('list');
          } else {
            // Show conflicts but still go back — rule was saved
            setRefreshKey(k => k + 1);
            setViewMode('list');
          }
        }
      } else if (viewMode === 'edit' && editingRule) {
        const payload: RuleUpdate = {
          name: name.trim(),
          description: description.trim() || undefined,
          isActive,
          conditionGroups,
          action,
        };
        const response = await rulesEngineService.updateRule(clientId, editingRule.id, payload);
        if (response.success && response.data) {
          setConflicts(response.data.conflicts || []);
          setRefreshKey(k => k + 1);
          setViewMode('list');
        }
      }
    } catch (err: any) {
      setAlertMessage(err.message || t('common.error', 'An error occurred'));
    } finally {
      setSaving(false);
    }
  };

  // ── Render ──

  if (initialLoading) {
    return (
      <div className="contracts-admin-panel">
        <div className="tab-loading">
          <div className="spinner" />
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (viewMode === 'list') {
    return (
      <div className="contracts-admin-panel">
        {/* Auto Contract Analysis toggle (KAN-195) */}
        <div className="auto-contract-card">
          <div className="setting-header">
            <h3>{t('admin.contracts.autoAnalysis.title', 'Automatic Contract Analysis')}</h3>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={autoContractSettings.enabled}
                disabled={autoContractSaving}
                onChange={(e) => handleAutoContractToggle(e.target.checked)}
              />
              <span className="slider"></span>
            </label>
          </div>
          <p>{t('admin.contracts.autoAnalysis.description', 'Automatically analyze contract PDFs received by email. Matches contracts to confirmed trades by counterparty trade number and runs automatic contract analysis.')}</p>

          {autoContractSettings.enabled && (
            <div className="auto-contract-fields">
              <div className="auto-contract-field">
                <label>{t('admin.contracts.autoAnalysis.lookbackDays', 'Lookback days')}</label>
                <input
                  type="number"
                  min={1}
                  max={90}
                  value={autoContractSettings.lookbackDays}
                  onChange={(e) => setAutoContractSettings(prev => ({ ...prev, lookbackDays: parseInt(e.target.value) || 10 }))}
                />
                <span className="field-hint">{t('admin.contracts.autoAnalysis.lookbackDaysHint', 'Number of days to look back for confirmed trades.')}</span>
              </div>
              <div className="auto-contract-field">
                <label>{t('admin.contracts.autoAnalysis.retryAttempts', 'Retry attempts')}</label>
                <input
                  type="number"
                  min={0}
                  max={10}
                  value={autoContractSettings.retryAttempts}
                  onChange={(e) => setAutoContractSettings(prev => ({ ...prev, retryAttempts: parseInt(e.target.value) || 3 }))}
                />
                <span className="field-hint">{t('admin.contracts.autoAnalysis.retryAttemptsHint', 'Number of retries if no immediate match is found.')}</span>
              </div>
              <div className="auto-contract-field">
                <label>{t('admin.contracts.autoAnalysis.retryDelayMinutes', 'Delay (min)')}</label>
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={autoContractSettings.retryDelayMinutes}
                  onChange={(e) => setAutoContractSettings(prev => ({ ...prev, retryDelayMinutes: parseInt(e.target.value) || 5 }))}
                />
                <span className="field-hint">{t('admin.contracts.autoAnalysis.retryDelayMinutesHint', 'Minutes to wait between each retry.')}</span>
              </div>
              <div className="auto-contract-save">
                <button
                  className="save-btn"
                  disabled={autoContractSaving}
                  onClick={handleAutoContractAdvancedSave}
                >
                  {autoContractSaving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Contract Signing Rules — only shown if digital signature is enabled */}
        {digitalSignatureEnabled && (
          <>
          {/* Conflict warning banner */}
          {conflicts.length > 0 && (
            <div className="conflict-warning-banner" style={{ marginBottom: '1rem' }}>
              <div className="conflict-warning-title">
                {t('rules.conflictWarning', 'Warning: Rule overlap detected')}
              </div>
              {conflicts.map((c, i) => (
                <div key={i} className="conflict-item">
                  "{c.conflictingRuleName}": {c.overlapDescription}
                </div>
              ))}
              <div className="conflict-dismiss-hint">
                {t('rules.conflictDismissHint', 'This is not an error. Multiple matching rules are allowed. Review your rules to ensure the overlap is intentional.')}
              </div>
              <button className="dismiss-btn" onClick={() => setConflicts([])}>
                {t('common.dismiss', 'Dismiss')}
              </button>
            </div>
          )}

          <RuleListView
            context={CONTEXT}
            title={t('admin.contracts.title', 'Reglas de Firma de Contratos')}
            onEdit={handleEdit}
            onCreate={handleCreate}
            onCopy={handleCopy}
            refreshKey={refreshKey}
            conditionSummaryFn={conditionSummaryFn}
            actionSummaryFn={actionSummaryFn}
          />
          </>
        )}

        {alertMessage && (
          <AlertModal message={alertMessage} onClose={() => setAlertMessage('')} />
        )}
      </div>
    );
  }

  // Is this the default / catch-all rule?
  const isDefault = viewMode === 'edit' && editingRule && editingRule.conditionGroups.length === 0;

  // Create / Edit form
  return (
    <div className="contracts-admin-panel">
      <div className="editor-header">
        <h3>
          {viewMode === 'create'
            ? t('rules.addRule', 'Add Rule')
            : t('rules.editRule', 'Edit Rule')}
        </h3>
      </div>

      <div className="editor-form">
        {/* Row 1: Name + Description + Active */}
        <div className="form-row-inline">
          <div className="form-field form-field-name">
            <label>{t('rules.name', 'Name')}</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={t('rules.namePlaceholder', 'e.g. BCI — Forward, Advanced Signature')}
              maxLength={200}
            />
          </div>

          <div className="form-field form-field-desc">
            <label>{t('rules.description', 'Description')} <span className="optional">({t('common.optional', 'optional')})</span></label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={t('rules.descriptionPlaceholder', 'Brief description')}
              maxLength={500}
            />
          </div>

          {!isDefault && (
            <div className="form-field form-field-active">
              <label className="active-checkbox-label">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={e => setIsActive(e.target.checked)}
                />
                {t('rules.isActive', 'Active')}
              </label>
            </div>
          )}
        </div>

        {/* Row 2: If (Conditions) → Then (Actions) */}
        <div className="if-then-layout">
          <div className="if-section">
            <div className="section-label">{t('rules.ifLabel', 'If')}</div>
            <RuleConditionBuilder
              conditionGroups={conditionGroups}
              fieldCatalogue={fieldCatalogue}
              onChange={isDefault ? () => {} : setConditionGroups}
            />
          </div>
          <div className="then-arrow">→</div>
          <div className="then-section">
            <div className="section-label">{t('rules.thenLabel', 'Then')}</div>
            <ContractSigningActionEditor
              action={action}
              onChange={setAction}
            />
          </div>
        </div>

        <div className="editor-actions">
          <button className="cancel-btn" onClick={handleBack} disabled={saving}>
            {t('common.cancel', 'Cancel')}
          </button>
          <button className="save-btn" onClick={handleSave} disabled={saving}>
            {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
          </button>
        </div>
      </div>

      {alertMessage && (
        <AlertModal message={alertMessage} onClose={() => setAlertMessage('')} />
      )}
    </div>
  );
};

export default ContractsAdminPanel;
